import { presentLexError } from '@/lib/user-notice';
import { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '@/lib/api';
import { useContentZoom } from '@/lib/useContentZoom';
import { ZOOM_VALUES } from '@/lib/contentZoom';

// ─── Types ────────────────────────────────────────────────────────────────────

type Tab = 'dumia' | 'search' | 'browse';
type Level = 'sections' | 'chapters' | 'partidas' | 'subpartidas' | 'fracciones';

interface TigieItem {
  id: string;
  codigo: string;
  descripcion: string;
  arancelImportacion?: string | number | null;
  arancelExportacion?: string | number | null;
  umt?: string | null;
  nicos?: Nico[];
  restricciones?: Restriccion[];
  preciosEstimados?: EstimatedPricePublication[];
  impactosDofia?: DofiaImpact[];
  tlcs?: TlcEntry[];
  aladi?: AladiEntry[];
  anexos?: Anexo[];
  observaciones?: Observacion[];
  level?: string;
  subpartida?: { codigo: string; descripcion: string; partida?: { codigo: string; descripcion: string } };
  capitulo?: { codigo: string; descripcion: string };
  jerarquia?: {
    subpartida?: { codigo: string; descripcion: string };
    partida?: { codigo: string; descripcion: string };
  };
}

interface EstimatedPricePublication {
  dofId: string;
  title?: string | null;
  date?: string | null;
  publishedAt?: string | null;
  url?: string;
  annexes?: string[];
  nicos?: string[];
  effectiveText?: string | null;
  restrictionCount?: number;
}

interface DofiaImpact {
  id: string;
  category: 'TARIFF' | 'RRNA';
  operation: 'ADD' | 'MODIFY' | 'REMOVE';
  field?: string | null;
  previousValue?: string | null;
  nextValue?: string | null;
  evidence: string;
  status: 'DETECTED' | 'REVIEW' | 'APPROVED' | 'REJECTED' | 'APPLIED';
  effectiveAt?: string | null;
  dofId: string;
  dofUrl: string;
  dofTitle?: string | null;
  publishedAt?: string | null;
}

interface Nico {
  id: string;
  codigo: string;
  descripcion: string;
}

interface Restriccion {
  id: string;
  tipo?: string;
  descripcion?: string;
  clave?: string;
  nombre?: string;
  rgceRules?: RgceRule[];
  rgceRuleCount?: number;
  seAnexos?: SeAnexoLink[];
  seAnexoCount?: number;
  seRules?: Array<{ lawId: string; articleId: string; ruleNumber: string }>;
}

interface RgceRule {
  lawId: string;
  articleId: string;
  ruleNumber: string;
  title?: string | null;
}

interface SeAnexoLink {
  lawId: string;
  annexNodeId: string;
  annexNumber: string;
  title?: string | null;
  matches?: Array<{
    nomCode: string;
    nicoCodes?: string[];
  }>;
}

interface TlcEntry {
  id: string;
  pais?: string;
  arancel?: string;
  tlc?: { nombre?: string };
}

interface AladiEntry {
  id: string;
  pais?: string;
  preferencia?: string;
  acuerdo?: { tipo?: string; numero?: string; paises?: string[] };
}

interface Anexo {
  id: string;
  numero?: string;
  descripcion?: string;
}

interface Observacion {
  id: string;
  tipo?: string;
  descripcion?: string;
}

interface SearchResults {
  partidas: TigieItem[];
  subpartidas: TigieItem[];
  fracciones: TigieItem[];
  nicos: any[];
  sugerencias?: TigieItem[];
}

interface Favorite {
  id: string;
  code: string;
  description: string;
  type: string;
}

interface MerchandiseProfile {
  nature: string | null;
  commercialDescription: string | null;
  primaryFunction: string | null;
  processedMaterials: string[];
  materialComposition: string[];
  intendedUse: string | null;
  technicalAttributes: Record<string, string>;
  facts: string[];
  inferences: string[];
  missingInformation: string[];
  clarificationQuestions: string[];
  confidence: number;
  needsClarification: boolean;
  source: 'document' | 'user' | 'fallback';
}

interface DumiaMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface DumiaClasificaResponse {
  reply: string;
  profile?: MerchandiseProfile;
  proposedFraction?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CHILD_LEVEL: Record<Level, Level | null> = {
  sections: 'chapters',
  chapters: 'partidas',
  partidas: 'subpartidas',
  subpartidas: 'fracciones',
  fracciones: null,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatArancel(val: string | number | null | undefined): string {
  if (val == null || val === '') return '—';
  const s = String(val).trim();
  if (s === '0' || s.toLowerCase() === 'ex.' || s.toLowerCase() === 'ex') return 'Ex.';
  if (s.endsWith('%')) return s;
  if (/^\d+(\.\d+)?$/.test(s)) return `${s}%`;
  return s;
}

function formatCode(codigo: string, level: Level): string {
  if (level === 'sections') return `Sec. ${codigo}`;
  if (level === 'chapters') return `Cap. ${codigo}`;
  if (level === 'partidas') return codigo.length === 4 ? `${codigo.slice(0, 2)}.${codigo.slice(2)}` : codigo;
  if (level === 'subpartidas') return codigo.length === 6 ? `${codigo.slice(0, 4)}.${codigo.slice(4)}` : codigo;
  if (level === 'fracciones') return codigo.length === 8 ? `${codigo.slice(0, 4)}.${codigo.slice(4, 6)}.${codigo.slice(6)}` : codigo;
  return codigo;
}

function codeFraccion(codigo: string): string {
  if (codigo.length === 8) return `${codigo.slice(0, 4)}.${codigo.slice(4, 6)}.${codigo.slice(6)}`;
  return codigo;
}

function formatDofDate(value?: string | null): string {
  if (!value) return 'Fecha no disponible';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-MX', {
    timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric',
  });
}

function readableDumiaReply(value: string): string {
  return value
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/^---$/gm, '')
    .replace(/^>\s?/gm, '')
    .trim();
}

// ─── Detail View ──────────────────────────────────────────────────────────────

function SectionHeader({ title }: { title: string }) {
  return (
    <div style={{ margin: '24px -16px 0', padding: '8px 16px', background: 'var(--color-surface)', borderTop: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)' }}>
      <p style={{ margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-accent)' }}>
        {title}
      </p>
    </div>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '1px solid var(--color-border-soft)', padding: '10px 0', gap: 12 }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-muted)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', textAlign: 'right', fontFamily: mono ? 'monospace' : undefined }}>{value}</span>
    </div>
  );
}

function DetailView({
  item: initialItem,
  onClose,
}: {
  item: TigieItem;
  isFav: boolean;
  onClose: () => void;
  onToggleFav: (item: TigieItem) => void;
}) {
  const [item, setItem] = useState<TigieItem>(initialItem);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    apiJson<TigieItem>(`/api/tigie?code=${initialItem.codigo}`)
      .then(data => setItem(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [initialItem.codigo]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeFraccion(item.codigo));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const handleExportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const W = 210, M = 18, CW = W - M * 2;
      let y = 0;

      // ─── Helpers ───────────────────────────────────────────────────────────
      const chk = () => { if (y > 270) { doc.addPage(); y = M; addPageHeader(); } };
      const t = (s: string, x: number, yy: number, opt?: any) => doc.text(s, x, yy, opt);

      const wrap = (s: string, maxW: number) => doc.splitTextToSize(s ?? '', maxW) as string[];

      const sectionBand = (title: string) => {
        chk();
        y += 4;
        doc.setFillColor(10, 22, 40);
        doc.rect(M, y, CW, 7, 'F');
        doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
        t(title.toUpperCase(), M + 3, y + 5);
        y += 10; doc.setTextColor(0, 0, 0);
      };

      const kv = (label: string, val: string, labelW = 62) => {
        chk();
        doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(110, 110, 110);
        t(label, M, y);
        doc.setFont('helvetica', 'normal'); doc.setTextColor(20, 20, 20);
        const valLines = wrap(val, CW - labelW);
        doc.text(valLines, M + labelW, y);
        y += Math.max(1, valLines.length) * 4.8;
        doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.1);
        doc.line(M, y, M + CW, y); y += 1.5;
      };

      // Treaty row: name (wrapping) on left, rate right-aligned — for TLC/ALADI
      const treatyRow = (nombre: string, pais: string | undefined, rate: string) => {
        chk();
        const rateStr = rate;
        const nameMaxW = CW - 18;

        doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(20, 20, 20);
        const nameLines = wrap(nombre, nameMaxW);
        doc.text(nameLines, M, y);

        doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 58, 138);
        t(rateStr, M + CW, y, { align: 'right' });

        y += nameLines.length * 4.5;

        if (pais) {
          doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(120, 120, 120);
          t(pais, M, y);
          y += 4;
        }

        doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.1);
        doc.line(M, y + 0.5, M + CW, y + 0.5); y += 2.5;
        chk();
      };

      // ─── Brand header (on page 1 only) ─────────────────────────────────────
      // Navy band
      doc.setFillColor(10, 22, 40);
      doc.rect(0, 0, W, 26, 'F');

      // Logo: "dumas" — "duma" white, "s" in accent
      doc.setFontSize(22); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
      t('duma', M, 17);
      const dumaW = doc.getTextWidth('duma');
      doc.setTextColor(147, 180, 255);
      t('s', M + dumaW, 17);

      // "Clasifica#" right-aligned in accent
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(147, 180, 255);
      t('Clasifica#', W - M, 17, { align: 'right' });

      y = 34;

      // ─── Hierarchy block ────────────────────────────────────────────────────
      const hier = item.jerarquia;
      const partida = hier?.partida ?? item.subpartida?.partida;
      const subpartida = hier?.subpartida ?? item.subpartida;

      // Partida row
      if (partida?.codigo) {
        const pCode = partida.codigo.length === 4
          ? `${partida.codigo.slice(0,2)}.${partida.codigo.slice(2)}`
          : partida.codigo;
        doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(110, 110, 110);
        t('PARTIDA', M, y);
        doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 58, 138);
        t(pCode, M + 22, y);
        doc.setFont('helvetica', 'normal'); doc.setTextColor(40, 40, 40);
        const pDesc = wrap(partida.descripcion ?? '', CW - 38);
        doc.text(pDesc, M + 36, y);
        y += Math.max(1, pDesc.length) * 4.5 + 1;
        doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.1);
        doc.line(M, y, M + CW, y); y += 2;
      }

      // Subpartida row
      if (subpartida?.codigo) {
        const sCode = subpartida.codigo.length === 6
          ? `${subpartida.codigo.slice(0,4)}.${subpartida.codigo.slice(4)}`
          : subpartida.codigo;
        doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(110, 110, 110);
        t('SUBPARTIDA', M, y);
        doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 58, 138);
        t(sCode, M + 26, y);
        doc.setFont('helvetica', 'normal'); doc.setTextColor(40, 40, 40);
        const sDesc = wrap(subpartida.descripcion ?? '', CW - 42);
        doc.text(sDesc, M + 40, y);
        y += Math.max(1, sDesc.length) * 4.5 + 1;
        doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.1);
        doc.line(M, y, M + CW, y); y += 2;
      }

      // Fracción — highlighted box
      y += 2;
      doc.setFillColor(245, 247, 252);
      doc.setDrawColor(30, 58, 138); doc.setLineWidth(0.4);
      const fCode = codeFraccion(item.codigo);
      const fDescLines = wrap(item.descripcion, CW - 44);
      const boxH = Math.max(1, fDescLines.length) * 5 + 10;
      doc.rect(M, y, CW, boxH, 'FD');

      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 58, 138);
      t('FRACCIÓN ARANCELARIA', M + 3, y + 5.5);
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(10, 22, 40);
      t(fCode, M + 3, y + 11);
      doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 30, 30);
      doc.text(fDescLines, M + 38, y + 11);
      y += boxH + 6;

      // ─── Page header (for continuation pages) ──────────────────────────────
      function addPageHeader() {
        doc.setFillColor(10, 22, 40);
        doc.rect(0, 0, W, 12, 'F');
        // Left: fracción info only
        doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(147, 180, 255);
        t(`Fracción ${fCode} · ${item.descripcion.slice(0, 60)}${item.descripcion.length > 60 ? '…' : ''}`, M, 8.5);
        // Right: "dumas" with "s" in accent, right-aligned
        doc.setFontSize(9); doc.setFont('helvetica', 'bold');
        const sW = doc.getTextWidth('s');
        doc.setTextColor(255, 255, 255);
        t('duma', W - M - sW, 8.5, { align: 'right' });
        doc.setTextColor(147, 180, 255);
        t('s', W - M, 8.5, { align: 'right' });
        y = M + 2;
      }

      doc.setLineWidth(0.2);

      const nicos = item.nicos ?? [];
      const restricciones = item.restricciones ?? [];
      const tlcs = item.tlcs ?? [];
      const aladi = item.aladi ?? [];
      const anexos = item.anexos ?? [];
      const observaciones = item.observaciones ?? [];

      // ─── NICOs ─────────────────────────────────────────────────────────────
      if (nicos.length > 0) {
        sectionBand(`Números de Identificación Comercial (NICOs) · ${nicos.length}`);
        nicos.forEach(n => {
          chk();
          // NICO label block
          doc.setFillColor(240, 244, 255);
          doc.rect(M, y - 1, CW, 6 + wrap(n.descripcion, CW - 18).length * 4.5, 'F');
          doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(10, 22, 40);
          t(n.codigo, M + 2, y + 4);
          doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 30, 30);
          const ls = wrap(n.descripcion, CW - 28);
          doc.text(ls, M + 22, y + 4);
          y += Math.max(1, ls.length) * 4.5 + 6;
          doc.setDrawColor(200, 210, 240); doc.setLineWidth(0.1);
          doc.line(M, y, M + CW, y); y += 2;
          chk();
        });
      }

      // ─── Aranceles ─────────────────────────────────────────────────────────
      sectionBand('Aranceles');
      kv('Importación general', formatArancel(item.arancelImportacion));
      kv('Exportación general', formatArancel(item.arancelExportacion));
      kv('Unidad de medida (UMT)', item.umt ?? '—');

      // ─── RRNA ──────────────────────────────────────────────────────────────
      if (restricciones.length > 0) {
        sectionBand(`Restricciones No Arancelarias (RRNA) · ${restricciones.length}`);
        restricciones.forEach(r => {
          chk();
          if (r.tipo) {
            doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(110, 110, 110);
            t(r.tipo, M, y); y += 4.5;
          }
          doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 30, 30);
          const ls = wrap(r.descripcion ?? '', CW);
          doc.text(ls, M, y); y += ls.length * 4.5 + 2;
          doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.1);
          doc.line(M, y, M + CW, y); y += 2;
          chk();
        });
      }

      // ─── TLCs ──────────────────────────────────────────────────────────────
      if (tlcs.length > 0) {
        sectionBand(`Tratados de Libre Comercio · ${tlcs.length}`);
        tlcs.forEach(t_ => {
          treatyRow(t_.tlc?.nombre ?? 'TLC', t_.pais, formatArancel(t_.arancel));
        });
      }

      // ─── ALADI ─────────────────────────────────────────────────────────────
      if (aladi.length > 0) {
        sectionBand(`ALADI · ${aladi.length}`);
        aladi.forEach(a => {
          const name = a.acuerdo ? `${a.acuerdo.tipo ?? 'ACE'} ${a.acuerdo.numero ?? ''}`.trim() : 'ALADI';
          treatyRow(name, a.pais, formatArancel(a.preferencia));
        });
      }

      // ─── Anexos ────────────────────────────────────────────────────────────
      if (anexos.length > 0) {
        sectionBand(`Anexos · ${anexos.length}`);
        anexos.forEach(a => {
          chk();
          if (a.numero) {
            doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(110, 110, 110);
            t(`Anexo ${a.numero}`, M, y); y += 4.5;
          }
          if (a.descripcion) {
            doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 30, 30);
            const ls = wrap(a.descripcion, CW);
            doc.text(ls, M, y); y += ls.length * 4.5 + 2;
          }
          doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.1);
          doc.line(M, y, M + CW, y); y += 2;
          chk();
        });
      }

      // ─── Notas Explicativas ────────────────────────────────────────────────
      if (observaciones.length > 0) {
        sectionBand(`Notas Explicativas · ${observaciones.length}`);
        observaciones.forEach(o => {
          chk();
          if (o.tipo) {
            doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(110, 110, 110);
            t(o.tipo, M, y); y += 4.5;
          }
          doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 30, 30);
          const ls = wrap(o.descripcion ?? '', CW);
          doc.text(ls, M, y); y += ls.length * 4.5 + 2;
          doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.1);
          doc.line(M, y, M + CW, y); y += 2;
          chk();
        });
      }

      // ─── Footer on every page ──────────────────────────────────────────────
      const total = doc.getNumberOfPages();
      for (let i = 1; i <= total; i++) {
        doc.setPage(i);
        // Bottom band
        doc.setFillColor(245, 245, 248);
        doc.rect(0, 285, W, 12, 'F');
        doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(130, 130, 130);
        t(`Fracción ${fCode} · TIGIE · dumas.solutions`, M, 291);
        doc.setFont('helvetica', 'bold'); doc.setTextColor(10, 22, 40);
        t(`${i} / ${total}`, W - M, 291, { align: 'right' });
      }

      const url = doc.output('bloburl');
      window.open(url as unknown as string, '_blank');
    } catch (e) {
      console.error('PDF error:', e);
    } finally {
      setExportingPdf(false);
    }
  };

  // Build breadcrumb path
  const path: string[] = [];
  const hier = item.jerarquia;
  if (hier?.partida?.codigo) {
    const p = hier.partida.codigo;
    path.push(`${p.slice(0, 2)}.${p.slice(2)}`);
  }
  if (hier?.subpartida?.codigo) {
    const s = hier.subpartida.codigo;
    path.push(`${s.slice(0, 4)}.${s.slice(4)}`);
  }
  if (!hier) {
    if (item.subpartida?.partida?.codigo) {
      const p = item.subpartida.partida.codigo;
      path.push(`${p.slice(0, 2)}.${p.slice(2)}`);
    }
    if (item.subpartida?.codigo) {
      const s = item.subpartida.codigo;
      path.push(`${s.slice(0, 4)}.${s.slice(4)}`);
    }
  }

  const nicos = item.nicos ?? [];
  const preciosEstimados = item.preciosEstimados ?? [];
  const impactosDofia = item.impactosDofia ?? [];
  const restricciones = item.restricciones ?? [];
  const tlcs = item.tlcs ?? [];
  const aladi = item.aladi ?? [];
  const anexos = item.anexos ?? [];
  const observaciones = item.observaciones ?? [];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'var(--color-bg)', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--color-border)', position: 'sticky', top: 0, background: 'var(--color-bg)', zIndex: 1 }}>
        <button
          type="button"
          onClick={onClose}
          style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-text)', fontSize: 20, lineHeight: 1, display: 'flex', alignItems: 'center', flexShrink: 0 }}
          aria-label="Volver"
        >
          ←
        </button>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)', letterSpacing: '-0.01em', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Fracción {codeFraccion(item.codigo)}
        </span>
        {/* Copy code */}
        <button
          type="button"
          onClick={handleCopy}
          style={{ padding: '5px 8px', border: '1px solid var(--color-border)', borderRadius: 7, background: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: copied ? 'var(--color-accent)' : 'var(--color-text-muted)', flexShrink: 0, letterSpacing: '0.02em', lineHeight: 1 }}
          aria-label="Copiar código"
        >
          {copied ? '✓ Copiado' : 'Copiar'}
        </button>
      </div>

      {/* body */}
      <div style={{ padding: '20px 16px', flex: 1 }}>
        {loading && (
          <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16 }}>Cargando…</p>
        )}

        {path.length > 0 && (
          <p style={{ margin: '0 0 12px', fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'monospace', letterSpacing: '0.04em' }}>
            {path.join(' › ')}
          </p>
        )}

        <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-accent)' }}>
          Fracción arancelaria
        </p>
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: 'var(--color-text)', lineHeight: 1.3, letterSpacing: '-0.02em' }}>
          {item.descripcion}
        </h2>

        {/* NICOs — primero */}
        {nicos.length > 0 && (
          <>
            <SectionHeader title={`NICOs · ${nicos.length}`} />
            {nicos.map(n => (
              <div key={n.id} style={{ borderBottom: '1px solid var(--color-border-soft)', padding: '10px 0', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700, color: 'var(--color-accent)', letterSpacing: '0.06em', flexShrink: 0, paddingTop: 1, minWidth: 24 }}>
                  {n.codigo}
                </span>
                <span style={{ fontSize: 13, color: 'var(--color-text)', lineHeight: 1.4 }}>
                  {n.descripcion}
                </span>
              </div>
            ))}
          </>
        )}

        {/* Aranceles */}
        <SectionHeader title="Aranceles" />
        <InfoRow label="Importación general" value={formatArancel(item.arancelImportacion)} />
        <InfoRow label="Exportación general" value={formatArancel(item.arancelExportacion)} />
        <InfoRow label="Unidad de medida (UMT)" value={item.umt ?? '—'} />
        <InfoRow label="Código" value={codeFraccion(item.codigo)} mono />

        {/* Precios estimados: only deterministic DOF-table matches are sent by the API. */}
        {preciosEstimados.length > 0 && (
          <>
            <SectionHeader title={`Precios estimados · ${preciosEstimados.length}`} />
            <p style={{ margin: '10px 0 2px', fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
              Publicaciones oficiales donde esta fracción aparece en la tabla. Confirma NICO y vigencia antes de operar.
            </p>
            {preciosEstimados.map((publication) => {
              const annexes = publication.annexes?.length
                ? `Anexo${publication.annexes.length > 1 ? 's' : ''} ${publication.annexes.join(' y ')}`
                : 'Precio estimado';
              const href = publication.dofId
                ? `https://dumas.solutions/dofia?pub=${encodeURIComponent(publication.dofId)}`
                : publication.url;
              return (
                <div key={publication.dofId} style={{ borderBottom: '1px solid var(--color-border-soft)', padding: '12px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: '0 0 5px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--lex-warning)' }}>
                        {annexes} · DOF {formatDofDate(publication.publishedAt ?? publication.date)}
                      </p>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, lineHeight: 1.45, color: 'var(--color-text)', overflowWrap: 'anywhere' }}>
                        {publication.title || 'Publicación del DOF'}
                      </p>
                      {publication.nicos?.length ? (
                        <p style={{ margin: '5px 0 0', fontSize: 11, lineHeight: 1.45, color: 'var(--color-text-muted)' }}>
                          NICO(s) en la tabla: {publication.nicos.join(', ')}.
                        </p>
                      ) : null}
                      {publication.effectiveText ? (
                        <p style={{ margin: '5px 0 0', fontSize: 11, lineHeight: 1.45, color: 'var(--color-text-muted)' }}>
                          <strong style={{ color: 'var(--color-text)' }}>Vigencia:</strong> {publication.effectiveText}
                        </p>
                      ) : null}
                    </div>
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ flexShrink: 0, border: '1px solid var(--color-border)', borderRadius: 8, padding: '7px 8px', color: 'var(--color-accent)', fontSize: 11, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}
                      >
                        Ver DOFiA ↗
                      </a>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {impactosDofia.length > 0 && (
          <>
            <SectionHeader title={`Impactos normativos DOFiA · ${impactosDofia.length}`} />
            <p style={{ margin: '10px 0 2px', fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
              Propuestas trazables de cambios arancelarios y RRNA. Sólo los impactos aplicados modifican el catálogo vigente.
            </p>
            {impactosDofia.map((impact) => {
              const status = ({ DETECTED: 'Detectado', REVIEW: 'Pendiente de revisión', APPROVED: 'Aprobado', REJECTED: 'Descartado', APPLIED: 'Aplicado' } as const)[impact.status];
              const operation = ({ ADD: 'Adición', MODIFY: 'Modificación', REMOVE: 'Eliminación' } as const)[impact.operation];
              const href = impact.dofId ? `https://dumas.solutions/dofia?pub=${encodeURIComponent(impact.dofId)}` : impact.dofUrl;
              return (
                <div key={impact.id} style={{ borderBottom: '1px solid var(--color-border-soft)', padding: '12px 0' }}>
                  <p style={{ margin: '0 0 5px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: impact.status === 'REJECTED' ? 'var(--lex-danger)' : impact.status === 'REVIEW' ? 'var(--lex-warning)' : 'var(--color-accent)' }}>
                    {impact.category === 'TARIFF' ? 'Tasa arancelaria' : 'RRNA'} · {operation} · {status}
                  </p>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, lineHeight: 1.45, color: 'var(--color-text)' }}>
                    {impact.nextValue || impact.dofTitle || 'Impacto normativo'}
                  </p>
                  {impact.effectiveAt ? <p style={{ margin: '5px 0 0', fontSize: 11, color: 'var(--color-text-muted)' }}>Vigencia: {formatDofDate(impact.effectiveAt)}</p> : null}
                  {href ? <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: 7, color: 'var(--color-accent)', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>Ver DOFiA ↗</a> : null}
                </div>
              );
            })}
          </>
        )}

        {/* RRNA */}
        {restricciones.length > 0 && (
          <>
            <SectionHeader title={`Restricciones No Arancelarias · ${restricciones.length}`} />
            {restricciones.map((r, i) => {
              const rgceRules = r.rgceRules ?? [];
              const rgceRuleCount = r.rgceRuleCount ?? rgceRules.length;
              const seAnexos = r.seAnexos ?? [];
              const seAnexoCount = r.seAnexoCount ?? seAnexos.length;
              const seRules = r.seRules ?? [];

              return (
              <div key={r.id ?? i} style={{ borderBottom: '1px solid var(--color-border-soft)', padding: '10px 0' }}>
                {r.tipo && (
                  <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                    {r.tipo}
                  </p>
                )}
                <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text)', lineHeight: 1.55 }}>
                  {r.descripcion ?? '—'}
                </p>
                {rgceRules.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 8 }}>
                    <span style={{ color: 'var(--color-text-muted)', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      LEX · RGCE
                    </span>
                    {rgceRules.map((rule) => (
                      <Link
                        key={`${rule.lawId}-${rule.articleId}`}
                        to={`/leyes/${encodeURIComponent(rule.lawId)}/articulo/${encodeURIComponent(rule.articleId)}`}
                        title={rule.title || `Abrir regla ${rule.ruleNumber} de las RGCE`}
                        aria-label={`Abrir regla RGCE ${rule.ruleNumber} en LEX`}
                        style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: '5px 7px', background: 'var(--lex-primary-soft)', color: 'var(--color-accent)', fontSize: 11, fontWeight: 700, lineHeight: 1.2, textDecoration: 'none' }}
                      >
                        RGCE {rule.ruleNumber}
                      </Link>
                    ))}
                    {rgceRuleCount > rgceRules.length && (
                      <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>
                        +{rgceRuleCount - rgceRules.length} citadas
                      </span>
                    )}
                  </div>
                )}
                {seAnexos.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 8 }}>
                    <span style={{ color: 'var(--color-text-muted)', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      LEX · SE
                    </span>
                    {seAnexos.map((annex) => {
                      const nomCodes = [...new Set((annex.matches ?? []).map((match) => match.nomCode).filter(Boolean))];
                      const nicoCodes = [...new Set((annex.matches ?? []).flatMap((match) => match.nicoCodes ?? []).filter(Boolean))];
                      const details = [
                        nomCodes.length ? `NOM coincidente: ${nomCodes.join(', ')}` : null,
                        nicoCodes.length ? `NICO(s) de la tabla: ${nicoCodes.join(', ')}` : null,
                        'La RRNA no identifica el NICO; verifica la acotación aplicable.',
                      ].filter(Boolean).join(' · ');
                      const href = `https://dumas.solutions/lex?law=${encodeURIComponent(annex.lawId)}&annex=${encodeURIComponent(annex.annexNodeId)}`;
                      return (
                        <a
                          key={`${annex.lawId}-${annex.annexNodeId}`}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`${annex.title || `Abrir Anexo ${annex.annexNumber} de la SE en LEX`}. ${details}`}
                          aria-label={`Abrir Anexo ${annex.annexNumber} de la Secretaría de Economía en LEX. ${details}`}
                          style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: '5px 7px', background: 'var(--lex-primary-soft)', color: 'var(--color-accent)', fontSize: 11, fontWeight: 700, lineHeight: 1.2, textDecoration: 'none' }}
                        >
                          Anexo {annex.annexNumber} · SE · verificar NICO ↗
                        </a>
                      );
                    })}
                    {seAnexoCount > seAnexos.length && (
                      <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>
                        +{seAnexoCount - seAnexos.length} contextos
                      </span>
                    )}
                  </div>
                )}
                {seRules.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 8 }}>
                    <span style={{ color: 'var(--color-text-muted)', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>LEX · SE</span>
                    {seRules.map((rule) => (
                      <Link key={`${rule.lawId}-${rule.articleId}`} to={`/leyes/${encodeURIComponent(rule.lawId)}/articulo/${encodeURIComponent(rule.articleId)}`} style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: '5px 7px', background: 'var(--lex-primary-soft)', color: 'var(--color-accent)', fontSize: 11, fontWeight: 700, lineHeight: 1.2, textDecoration: 'none' }}>
                        Regla {rule.ruleNumber} · SE
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              );
            })}
          </>
        )}

        {/* TLCs — one row per country partner */}
        {tlcs.length > 0 && (
          <>
            <SectionHeader title={`Tratados de Libre Comercio · ${tlcs.length}`} />
            {tlcs.map((t, i) => (
              <div key={t.id ?? i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--color-border-soft)', padding: '10px 0', gap: 12 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text)', fontWeight: 500 }}>
                    {t.tlc?.nombre ?? 'TLC'}
                  </p>
                  {t.pais && (
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--color-text-muted)' }}>
                      {t.pais}
                    </p>
                  )}
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-accent)', flexShrink: 0 }}>
                  {formatArancel(t.arancel)}
                </span>
              </div>
            ))}
          </>
        )}

        {/* ALADI */}
        {aladi.length > 0 && (
          <>
            <SectionHeader title={`ALADI · ${aladi.length}`} />
            {aladi.map((a, i) => (
              <div key={a.id ?? i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--color-border-soft)', padding: '10px 0', gap: 12 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text)', fontWeight: 500 }}>
                    {a.acuerdo ? `${a.acuerdo.tipo ?? 'ACE'} ${a.acuerdo.numero ?? ''}`.trim() : 'ALADI'}
                  </p>
                  {a.pais && (
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--color-text-muted)' }}>
                      {a.pais}
                    </p>
                  )}
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-accent)', flexShrink: 0 }}>
                  {formatArancel(a.preferencia)}
                </span>
              </div>
            ))}
          </>
        )}

        {/* Anexos */}
        {anexos.length > 0 && (
          <>
            <SectionHeader title={`Anexos · ${anexos.length}`} />
            {anexos.map((a, i) => (
              <div key={a.id ?? i} style={{ borderBottom: '1px solid var(--color-border-soft)', padding: '10px 0' }}>
                {a.numero && (
                  <p style={{ margin: '0 0 3px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                    Anexo {a.numero}
                  </p>
                )}
                {a.descripcion && (
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text)', lineHeight: 1.55 }}>
                    {a.descripcion}
                  </p>
                )}
              </div>
            ))}
          </>
        )}

        {/* Observaciones / Notas Explicativas */}
        {observaciones.length > 0 && (
          <>
            <SectionHeader title={`Notas Explicativas · ${observaciones.length}`} />
            {observaciones.map((o, i) => (
              <div key={o.id ?? i} style={{ borderBottom: '1px solid var(--color-border-soft)', padding: '10px 0' }}>
                {o.tipo && (
                  <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                    {o.tipo}
                  </p>
                )}
                <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text)', lineHeight: 1.55 }}>
                  {o.descripcion ?? '—'}
                </p>
              </div>
            ))}
          </>
        )}

        {/* Actions */}
        <div style={{ margin: '28px -16px 0', padding: '0 16px 32px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Export PDF */}
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={exportingPdf || loading}
            style={{
              width: '100%', padding: '14px 0', border: 'none',
              borderRadius: 10, background: 'var(--color-navy)', fontSize: 14, fontWeight: 700,
              color: 'var(--color-bg)', cursor: exportingPdf ? 'wait' : 'pointer',
              letterSpacing: '-0.01em', opacity: loading ? 0.5 : 1,
            }}
          >
            {exportingPdf ? 'Generando PDF…' : 'Exportar PDF'}
          </button>
          {/* AI placeholder */}
          <button
            type="button"
            disabled
            style={{
              width: '100%', padding: '14px 0', border: '1px solid var(--color-border)',
              borderRadius: 10, background: 'transparent', fontSize: 14, fontWeight: 600,
              color: 'var(--color-text-muted)', cursor: 'not-allowed', letterSpacing: '-0.01em',
            }}
          >
            Clasificar con IA — Próximamente
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Accordion Node ───────────────────────────────────────────────────────────

interface AccordionNodeProps {
  item: TigieItem;
  level: Level;
  depth: number;
  expandedItems: Set<string>;
  childrenCache: Map<string, TigieItem[]>;
  loadingItems: Set<string>;
  favorites: Favorite[];
  onToggle: (item: TigieItem, level: Level) => void;
  onView: (item: TigieItem) => void;
  onToggleFav: (item: TigieItem) => void;
}

function AccordionNode({
  item,
  level,
  depth,
  expandedItems,
  childrenCache,
  loadingItems,
  favorites,
  onToggle,
  onView,
  onToggleFav,
}: AccordionNodeProps) {
  const childLevel = CHILD_LEVEL[level];
  const isFraccion = level === 'fracciones';
  const isExpanded = expandedItems.has(item.id);
  const children = childrenCache.get(item.id) ?? null;
  const isLoading = loadingItems.has(item.id);
  const baseIndent = depth * 16;

  return (
    <>
      <div
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: `10px 4px 10px ${baseIndent}px`,
          borderBottom: '1px solid var(--color-border-soft)',
          cursor: 'pointer',
          background: isExpanded && !isFraccion ? 'var(--color-surface)' : undefined,
        }}
        onClick={() => isFraccion ? onView(item) : onToggle(item, level)}
      >
        {!isFraccion && (
          <span style={{
            fontSize: 12, color: isExpanded ? 'var(--color-accent)' : 'var(--color-text-muted)',
            flexShrink: 0, marginTop: 2,
            transform: isExpanded ? 'rotate(90deg)' : undefined,
            display: 'inline-block', transition: 'transform 0.15s',
          }}>
            ›
          </span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            fontSize: 10, fontFamily: 'monospace', fontWeight: 700,
            letterSpacing: '0.06em', color: isExpanded ? 'var(--color-accent)' : 'var(--color-text-muted)',
            textTransform: 'uppercase',
          }}>
            {formatCode(item.codigo, level)}
          </span>
          <p style={{
            margin: '2px 0 0', fontSize: 13,
            fontWeight: isExpanded ? 600 : 400,
            color: 'var(--color-text)', lineHeight: 1.35,
            overflow: depth < 2 ? 'hidden' : undefined,
            display: depth < 2 ? '-webkit-box' : undefined,
            WebkitLineClamp: depth < 2 ? 2 : undefined,
            WebkitBoxOrient: depth < 2 ? 'vertical' as const : undefined,
          }}>
            {item.descripcion}
          </p>
          {isFraccion && (
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontWeight: 600 }}>
                IMP {formatArancel(item.arancelImportacion)}
              </span>
              {item.umt && (
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>· {item.umt}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {!isFraccion && isExpanded && childLevel && (
        isLoading ? (
          <div style={{ padding: `8px 4px 8px ${baseIndent + 16}px`, fontSize: 12, color: 'var(--color-text-muted)' }}>
            Cargando…
          </div>
        ) : (children ?? []).length === 0 ? (
          <div style={{ padding: `8px 4px 8px ${baseIndent + 16}px`, fontSize: 12, color: 'var(--color-text-muted)' }}>
            Sin elementos
          </div>
        ) : (children ?? []).map(child => (
          <AccordionNode
            key={child.id}
            item={child}
            level={childLevel}
            depth={depth + 1}
            expandedItems={expandedItems}
            childrenCache={childrenCache}
            loadingItems={loadingItems}
            favorites={favorites}
            onToggle={onToggle}
            onView={onView}
            onToggleFav={onToggleFav}
          />
        ))
      )}
    </>
  );
}

// ─── Search Result Row ────────────────────────────────────────────────────────

function SearchResultRow({
  item,
  onView,
}: {
  item: TigieItem;
  isFav: boolean;
  onView: (item: TigieItem) => void;
  onToggleFav: (item: TigieItem) => void;
}) {
  const path: string[] = [];
  if (item.subpartida?.partida?.codigo) {
    const p = item.subpartida.partida.codigo;
    path.push(`${p.slice(0, 2)}.${p.slice(2)}`);
  }
  if (item.subpartida?.codigo) {
    const s = item.subpartida.codigo;
    path.push(`${s.slice(0, 4)}.${s.slice(4)}`);
  }

  return (
    <div
      style={{ borderBottom: '1px solid var(--color-border-soft)', padding: '14px 0', cursor: 'pointer' }}
      onClick={() => onView(item)}
    >
      {path.length > 0 && (
        <p style={{ margin: '0 0 4px', fontSize: 10, fontFamily: 'monospace', color: 'var(--color-text-muted)', letterSpacing: '0.04em' }}>
          {path.join(' › ')}
        </p>
      )}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 10, fontFamily: 'monospace', fontWeight: 700, color: 'var(--color-accent)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {codeFraccion(item.codigo)}
          </span>
          <p style={{ margin: '3px 0 0', fontSize: 14, fontWeight: 500, color: 'var(--color-text)', lineHeight: 1.4 }}>
            {item.descripcion}
          </p>
          {(item.arancelImportacion != null || item.umt) && (
            <div style={{ display: 'flex', gap: 8, marginTop: 5 }}>
              {item.arancelImportacion != null && (
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontWeight: 600 }}>
                  IMP {formatArancel(item.arancelImportacion)}
                </span>
              )}
              {item.umt && (
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>· {item.umt}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const DUMIA_SESSION_KEY = 'lex-clasifica-dumia-session-v1';

function ProfileValue({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <p style={{ margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>{label}</p>
      <p style={{ margin: '3px 0 0', fontSize: 13, lineHeight: 1.45, color: 'var(--color-text)' }}>{value || 'Pendiente'}</p>
    </div>
  );
}

function MerchandiseProfilePanel({ profile }: { profile: MerchandiseProfile }) {
  const materials = [...profile.materialComposition, ...profile.processedMaterials];
  return (
    <details open style={{ border: '1px solid var(--color-border)', borderRadius: 12, background: 'var(--color-surface)', overflow: 'hidden' }}>
      <summary style={{ cursor: 'pointer', padding: '12px 14px', fontSize: 13, fontWeight: 700, color: 'var(--color-text)' }}>
        Ficha merceológica · {Math.round(profile.confidence * 100)}% construida
      </summary>
      <div style={{ borderTop: '1px solid var(--color-border)', padding: 14, display: 'grid', gap: 14 }}>
        <ProfileValue label="Mercancía" value={profile.commercialDescription || profile.nature} />
        <ProfileValue label="Función principal" value={profile.primaryFunction} />
        <ProfileValue label="Material o composición" value={materials.join(', ') || null} />
        <ProfileValue label="Uso" value={profile.intendedUse} />
        {Object.keys(profile.technicalAttributes).length > 0 && (
          <ProfileValue
            label="Características técnicas"
            value={Object.entries(profile.technicalAttributes).map(([key, value]) => `${key}: ${value}`).join(' · ')}
          />
        )}
        {profile.inferences.length > 0 && (
          <ProfileValue label="Por confirmar" value={profile.inferences.join(' · ')} />
        )}
      </div>
    </details>
  );
}

function DumiaClassification() {
  const [messages, setMessages] = useState<DumiaMessage[]>([]);
  const [profile, setProfile] = useState<MerchandiseProfile | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const nextQuestion = profile?.needsClarification ? profile.clarificationQuestions[0] : null;

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(DUMIA_SESSION_KEY) || 'null');
      if (Array.isArray(saved?.messages)) setMessages(saved.messages);
      if (saved?.profile) setProfile(saved.profile);
    } catch {}
  }, []);

  useEffect(() => {
    if (messages.length > 0 || profile) {
      localStorage.setItem(DUMIA_SESSION_KEY, JSON.stringify({ messages, profile }));
    }
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, profile]);

  const submitContent = async (content: string, skipClarification = false) => {
    if (!content || sending) return;
    const nextMessages: DumiaMessage[] = [...messages, { role: 'user', content }];
    setMessages(nextMessages);
    setDraft('');
    setSending(true);
    setError('');
    try {
      const response = await apiJson<DumiaClasificaResponse>('/api/clasifica/chat', {
        method: 'POST',
        body: JSON.stringify({ messages: nextMessages, profile, skipClarification }),
      });
      if (response.profile) setProfile(response.profile);
      setMessages(current => [...current, { role: 'assistant', content: response.reply }]);
    } catch {
      setError('No pude actualizar la ficha. Tu descripción quedó guardada; intenta enviarla de nuevo.');
    } finally {
      setSending(false);
    }
  };

  const send = async (event?: React.FormEvent) => {
    event?.preventDefault();
    await submitContent(draft.trim());
  };

  const startOver = () => {
    setMessages([]);
    setProfile(null);
    setDraft('');
    setError('');
    localStorage.removeItem(DUMIA_SESSION_KEY);
  };

  return (
    <section style={{ display: 'grid', gap: 14 }}>
      <div style={{ padding: '14px 16px', border: '1px solid var(--color-border)', borderRadius: 12, background: 'var(--color-surface)' }}>
        <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--color-text)' }}>Describe la mercancía con tus palabras</p>
        <p style={{ margin: '5px 0 0', fontSize: 13, lineHeight: 1.5, color: 'var(--color-text-muted)' }}>
          DumiA Core construye la ficha mientras conversas. No necesitas llenar un cuestionario.
        </p>
      </div>

      {profile && <MerchandiseProfilePanel profile={profile} />}

      {nextQuestion && (
        <div style={{ padding: 14, border: '1px solid var(--lex-warning, #d97706)', borderRadius: 12, background: 'var(--color-surface)', display: 'grid', gap: 10 }}>
          <div>
            <p style={{ margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--lex-warning, #d97706)' }}>Dato que puede cambiar la clasificación</p>
            <p style={{ margin: '5px 0 0', fontSize: 14, fontWeight: 600, lineHeight: 1.45, color: 'var(--color-text)' }}>{nextQuestion}</p>
            {profile?.missingInformation[0] && (
              <p style={{ margin: '5px 0 0', fontSize: 12, lineHeight: 1.45, color: 'var(--color-text-muted)' }}>
                DumiA lo solicita porque falta confirmar {profile.missingInformation[0]} y la respuesta puede cambiar la partida o subpartida.
              </p>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              type="button"
              onClick={() => inputRef.current?.focus()}
              style={{ border: 0, borderRadius: 8, padding: '8px 11px', background: 'var(--color-navy)', color: 'var(--color-bg)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
            >
              Responder ahora
            </button>
            <button
              type="button"
              disabled={sending}
              onClick={() => submitContent(`No cuento con ese dato para responder: ${nextQuestion}`, true)}
              style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: '8px 11px', background: 'transparent', color: 'var(--color-text-muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              No cuento con ese dato
            </button>
          </div>
        </div>
      )}

      <div aria-live="polite" style={{ display: 'grid', gap: 10 }}>
        {messages.length === 0 && (
          <div style={{ padding: '12px 14px', borderRadius: 12, background: 'var(--color-primary-soft, rgba(30,58,138,0.08))', color: 'var(--color-text)', fontSize: 13, lineHeight: 1.5 }}>
            Cuéntame qué es, para qué sirve y cómo se presenta. Con lo que sepas basta para empezar.
          </div>
        )}
        {messages.map((message, index) => (
          <div
            key={`${message.role}-${index}`}
            style={{
              justifySelf: message.role === 'user' ? 'end' : 'start',
              maxWidth: '88%', padding: '10px 12px', borderRadius: 12,
              background: message.role === 'user' ? 'var(--color-navy)' : 'var(--color-surface)',
              color: message.role === 'user' ? 'var(--color-bg)' : 'var(--color-text)',
              border: message.role === 'assistant' ? '1px solid var(--color-border)' : 'none',
              whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.55,
            }}
          >
            {message.role === 'assistant' ? readableDumiaReply(message.content) : message.content}
          </div>
        ))}
        {sending && <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted)' }}>DumiA está actualizando la ficha…</p>}
        <div ref={endRef} />
      </div>

      {error && <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--lex-danger)' }}>{presentLexError(error)}</p>}

      <form onSubmit={send} style={{ display: 'grid', gap: 8 }}>
        <label htmlFor="dumia-merchandise" style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)' }}>Añade o corrige información</label>
        <textarea
          ref={inputRef}
          id="dumia-merchandise"
          value={draft}
          onChange={event => setDraft(event.target.value)}
          placeholder="Ej. Es una bomba eléctrica para agua, de acero inoxidable…"
          rows={3}
          style={{ resize: 'vertical', boxSizing: 'border-box', width: '100%', padding: 12, border: '1px solid var(--color-border)', borderRadius: 10, background: 'var(--color-surface)', color: 'var(--color-text)', font: 'inherit', fontSize: 16, lineHeight: 1.45 }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          {(messages.length > 0 || profile) ? (
            <button type="button" onClick={startOver} style={{ border: 0, background: 'transparent', color: 'var(--color-text-muted)', padding: '9px 0', fontSize: 12, cursor: 'pointer' }}>Empezar otra ficha</button>
          ) : <span />}
          <button type="submit" disabled={!draft.trim() || sending} style={{ border: 0, borderRadius: 10, padding: '10px 16px', background: 'var(--color-navy)', color: 'var(--color-bg)', fontSize: 13, fontWeight: 700, opacity: !draft.trim() || sending ? 0.5 : 1, cursor: 'pointer' }}>
            {sending ? 'Actualizando…' : profile ? 'Actualizar ficha' : 'Crear ficha'}
          </button>
        </div>
      </form>
    </section>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function ClasificaPage() {
  const zoom = useContentZoom();
  const [tab, setTab] = useState<Tab>('dumia');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [childrenCache, setChildrenCache] = useState<Map<string, TigieItem[]>>(new Map());
  const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set());
  const [rootSections, setRootSections] = useState<TigieItem[] | null>(null);
  const [loadingRoot, setLoadingRoot] = useState(false);

  const [viewingItem, setViewingItem] = useState<TigieItem | null>(null);
  const [favorites, setFavorites] = useState<Favorite[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    apiJson<Favorite[]>('/api/clasifica/favorites').then(setFavorites).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'browse' && rootSections === null && !loadingRoot) {
      setLoadingRoot(true);
      apiJson<{ data: TigieItem[] }>('/api/tigie?level=sections&limit=999')
        .then(res => setRootSections(res.data ?? []))
        .catch(() => setRootSections([]))
        .finally(() => setLoadingRoot(false));
    }
  }, [tab, rootSections, loadingRoot]);

  const handleSearch = useCallback(async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setSearchError('');
    try {
      const data = await apiJson<SearchResults>(`/api/tigie?search=${encodeURIComponent(q)}&limit=20`);
      setResults(data);
    } catch {
      setSearchError('Error al buscar. Intenta de nuevo.');
    } finally {
      setSearching(false);
    }
  }, [query]);

  const handleToggle = useCallback(async (item: TigieItem, level: Level) => {
    const childLevel = CHILD_LEVEL[level];
    if (!childLevel) return;

    const willOpen = !expandedItems.has(item.id);
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });

    if (willOpen && !childrenCache.has(item.id)) {
      setLoadingItems(prev => new Set(prev).add(item.id));
      try {
        const data = await apiJson<{ data: TigieItem[] }>(
          `/api/tigie?level=${childLevel}&parentId=${item.id}&limit=999`
        );
        setChildrenCache(prev => new Map(prev).set(item.id, data.data ?? []));
      } catch {
        setChildrenCache(prev => new Map(prev).set(item.id, []));
      } finally {
        setLoadingItems(prev => { const next = new Set(prev); next.delete(item.id); return next; });
      }
    }
  }, [childrenCache, expandedItems]);

  const handleToggleFav = useCallback(async (item: TigieItem) => {
    const existing = favorites.find(f => f.code === item.codigo);
    if (existing) {
      try {
        await apiJson(`/api/clasifica/favorites/${existing.id}`, { method: 'DELETE' });
        setFavorites(prev => prev.filter(f => f.id !== existing.id));
      } catch {}
    } else {
      try {
        const fav = await apiJson<Favorite>('/api/clasifica/favorites', {
          method: 'POST',
          body: JSON.stringify({
            code: item.codigo,
            description: item.descripcion,
            type: 'fraccion',
            associationType: 'personal',
            associationId: 'personal',
            associationName: 'Mis favoritos',
          }),
        });
        setFavorites(prev => [...prev, fav]);
      } catch {}
    }
  }, [favorites]);

  const handleClear = () => {
    setQuery('');
    setResults(null);
    setSearchError('');
    inputRef.current?.focus();
  };

  const allResults: TigieItem[] = results
    ? [
        ...(results.fracciones ?? []),
        ...(results.nicos?.map((n: any) => ({
          ...n,
          codigo: n.fraccionPadre ? n.fraccionPadre.codigo : n.codigo,
          descripcion: n.descripcion,
          arancelImportacion: n.fraccionPadre?.arancelImportacion ?? n.arancelImportacion,
          umt: n.fraccionPadre?.umt ?? n.umt,
          subpartida: n.fraccionPadre?.subpartida,
        })) ?? []),
        ...(results.sugerencias ?? []),
      ]
    : [];

  const searchPartidas: TigieItem[] = results?.partidas ?? [];
  const searchSubpartidas: TigieItem[] = results?.subpartidas ?? [];

  return (
    <>
      {viewingItem && (
        <DetailView
          item={viewingItem}
          isFav={favorites.some(f => f.code === viewingItem.codigo)}
          onClose={() => setViewingItem(null)}
          onToggleFav={handleToggleFav}
        />
      )}

      <main className="lex-page" style={{ zoom: ZOOM_VALUES[zoom] } as React.CSSProperties}>
        <div style={{ padding: '4px 0 16px' }}>
          <p style={{ margin: 0, fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
            TIGIE
          </p>
          <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--color-text)' }}>
            Clasifica#
          </h1>
        </div>

        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'var(--color-surface)', borderRadius: 10, padding: 4 }}>
          {(['dumia', 'search', 'browse'] as Tab[]).map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              style={{
                flex: 1, padding: '8px 0', border: 'none', borderRadius: 8, cursor: 'pointer',
                fontSize: 13, fontWeight: 600, letterSpacing: '-0.01em',
                background: tab === t ? 'var(--color-navy)' : 'transparent',
                color: tab === t ? 'var(--color-bg)' : 'var(--color-text-muted)',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {t === 'dumia' ? 'Con DumiA' : t === 'search' ? 'Buscar' : 'TIGIE'}
            </button>
          ))}
        </div>

        {tab === 'dumia' && <DumiaClassification />}

        {/* ── Search tab ── */}
        {tab === 'search' && (
          <>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  ref={inputRef}
                  type="search"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Código o descripción…"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 36px 10px 14px',
                    border: '1px solid var(--color-border)', borderRadius: 10,
                    fontSize: 16, background: 'var(--color-surface)', color: 'var(--color-text)',
                    outline: 'none',
                  }}
                />
                {query && (
                  <button
                    type="button"
                    onClick={handleClear}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 16, lineHeight: 1 }}
                  >
                    ×
                  </button>
                )}
              </div>
              <button
                type="submit"
                disabled={searching || !query.trim()}
                style={{
                  padding: '10px 18px', border: 'none', borderRadius: 10, cursor: 'pointer',
                  fontSize: 13, fontWeight: 700, background: 'var(--color-navy)', color: 'var(--color-bg)',
                  opacity: searching || !query.trim() ? 0.5 : 1,
                  flexShrink: 0,
                }}
              >
                {searching ? '…' : 'Buscar'}
              </button>
            </form>

            {searchError && (
              <p style={{ fontSize: 13, color: 'var(--lex-danger)', marginBottom: 12 }}>{presentLexError(searchError)}</p>
            )}

            {results && allResults.length === 0 && searchPartidas.length === 0 && searchSubpartidas.length === 0 && (
              <p style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>Sin resultados para «{query}».</p>
            )}

            {/* Fracciones — leaf results, direct tap to detail */}
            {allResults.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <p style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                  Fracciones · {allResults.length}
                </p>
                {allResults.map(item => (
                  <SearchResultRow
                    key={item.id}
                    item={item}
                    isFav={favorites.some(f => f.code === item.codigo)}
                    onView={setViewingItem}
                    onToggleFav={handleToggleFav}
                  />
                ))}
              </div>
            )}

            {/* Partidas — expandable accordion: partida → subpartidas → fracciones */}
            {searchPartidas.length > 0 && (
              <div style={{ marginTop: allResults.length > 0 ? 20 : 0 }}>
                <p style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                  Partidas · {searchPartidas.length}
                </p>
                {searchPartidas.map(item => (
                  <AccordionNode
                    key={item.id}
                    item={item}
                    level="partidas"
                    depth={0}
                    expandedItems={expandedItems}
                    childrenCache={childrenCache}
                    loadingItems={loadingItems}
                    favorites={favorites}
                    onToggle={handleToggle}
                    onView={setViewingItem}
                    onToggleFav={handleToggleFav}
                  />
                ))}
              </div>
            )}

            {/* Subpartidas — expandable accordion: subpartida → fracciones */}
            {searchSubpartidas.length > 0 && (
              <div style={{ marginTop: searchPartidas.length > 0 || allResults.length > 0 ? 20 : 0 }}>
                <p style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                  Subpartidas · {searchSubpartidas.length}
                </p>
                {searchSubpartidas.map(item => (
                  <AccordionNode
                    key={item.id}
                    item={item}
                    level="subpartidas"
                    depth={0}
                    expandedItems={expandedItems}
                    childrenCache={childrenCache}
                    loadingItems={loadingItems}
                    favorites={favorites}
                    onToggle={handleToggle}
                    onView={setViewingItem}
                    onToggleFav={handleToggleFav}
                  />
                ))}
              </div>
            )}

            {!results && !searching && (
              <div style={{ paddingTop: 24, textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
                  Busca por descripción de mercancía o código arancelario
                </p>
              </div>
            )}
          </>
        )}

        {/* ── Browse tab ── */}
        {tab === 'browse' && (
          <div>
            {loadingRoot ? (
              <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Cargando secciones…</p>
            ) : (rootSections ?? []).length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Sin datos.</p>
            ) : (
              (rootSections ?? []).map(section => (
                <AccordionNode
                  key={section.id}
                  item={section}
                  level="sections"
                  depth={0}
                  expandedItems={expandedItems}
                  childrenCache={childrenCache}
                  loadingItems={loadingItems}
                  favorites={favorites}
                  onToggle={handleToggle}
                  onView={setViewingItem}
                  onToggleFav={handleToggleFav}
                />
              ))
            )}
          </div>
        )}
      </main>
    </>
  );
}
